/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {
    BarChart3,
    LayersPlus,
    type LucideIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { EeCheckApiError, eeCheckApi } from '@/api/eeCheckApi';
import StatusModal from '@/components/feedback/StatusModal';
import {
    ElectricityIcon,
    GasIcon,
    HeatIcon,
    PhotovoltaicsIcon,
    WaterIcon,
} from '@/components/icon/SupplyIcon';
import { HAS_NEUTRAUBLING_SENSOR_DATA } from '@/config/csvSensors';
import { MUENSTER_DEVICE_SENSOR_CONFIG } from '@/config/deviceSensorConfig';
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import type {
    BuildingStatisticsData,
    BuildingStatisticsSupply,
    BuildingStatisticsSupplyData,
    ConfigurationTags,
} from '@/types/eeCheck';

type BuildingStatisticsCustomer = ConfigurationTags['customer'];
type BuildingStatisticsSupplyKey = Exclude<keyof BuildingStatisticsData, 'building'>;
type BuildingStatisticsSeriesKey = 'daily_W' | 'yearly_W' | 'monthly_W' | 'daily_W_Abs';
type BuildingStatisticsSupplyAccent = 'electricity' | 'gas' | 'grid-feed' | 'heat' | 'photovoltaics' | 'water';

interface BuildingStatisticsSeriesSummary {
    firstTimestamp?: number;
    firstValue?: number;
    lastTimestamp?: number;
    lastValue?: number;
    timestampCount: number;
    unit: string;
    valueCount: number;
}

interface BuildingStatisticsSupplySummary {
    series: Record<BuildingStatisticsSeriesKey, BuildingStatisticsSeriesSummary>;
    urn: string;
}

type BuildingStatisticsSummary = Record<
    BuildingStatisticsSupplyKey,
    BuildingStatisticsSupplySummary | null
>;

const SUPPLY_TYPES: {
    accent: BuildingStatisticsSupplyAccent;
    icon: LucideIcon;
    key: BuildingStatisticsSupplyKey;
    label: string;
}[] = [
    { accent: 'photovoltaics', icon: PhotovoltaicsIcon, key: 'pv', label: 'Photovoltaics' },
    { accent: 'electricity', icon: ElectricityIcon, key: 'grid_supply', label: 'Electricity' },
    { accent: 'heat', icon: HeatIcon, key: 'heat', label: 'Heat' },
    { accent: 'water', icon: WaterIcon, key: 'water', label: 'Water' },
    { accent: 'gas', icon: GasIcon, key: 'gas', label: 'Gas' },
    { accent: 'grid-feed', icon: LayersPlus, key: 'grid_feed', label: 'Grid feed' },
];

const SERIES_TYPES: BuildingStatisticsSeriesKey[] = [
    'daily_W',
    'monthly_W',
    'yearly_W',
    'daily_W_Abs',
];

function getMuensterSystems(): string[] {
    const catalog = new DeviceSensorCatalog(MUENSTER_DEVICE_SENSOR_CONFIG);

    return Array.from(new Set(
        catalog.getVirtualDevices()
            .map((device) => catalog.getDeviceSystem(device))
            .filter((system): system is string => system !== null),
    )).sort((left, right) => left.localeCompare(right));
}

function isAvailableSupply(supply: BuildingStatisticsSupplyData): supply is BuildingStatisticsSupply {
    return !Array.isArray(supply);
}

function summarizeBuildingStatistics(data: BuildingStatisticsData): BuildingStatisticsSummary {
    return Object.fromEntries(SUPPLY_TYPES.map(({ key }) => {
        const supply = data[key];

        if (!isAvailableSupply(supply)) {
            return [key, null];
        }

        const series = Object.fromEntries(SERIES_TYPES.map((seriesKey) => {
            const source = supply[seriesKey];
            const lastIndex = Math.min(source.timestamps.length, source.values.length) - 1;

            return [seriesKey, {
                firstTimestamp: source.timestamps[0],
                firstValue: source.values[0],
                lastTimestamp: source.timestamps[lastIndex],
                lastValue: source.values[lastIndex],
                timestampCount: source.timestamps.length,
                unit: source.unit,
                valueCount: source.values.length,
            }];
        })) as Record<BuildingStatisticsSeriesKey, BuildingStatisticsSeriesSummary>;

        return [key, { series, urn: supply.urn }];
    })) as BuildingStatisticsSummary;
}

function formatSeriesPoint(
    timestamp: number | undefined,
    value: number | undefined,
    unit: string,
    locale: string,
    unavailableText: string,
): string {
    if (
        timestamp === undefined
        || value === undefined
        || !Number.isFinite(timestamp)
        || !Number.isFinite(value)
    ) {
        return unavailableText;
    }

    const date = new Date(timestamp * 1000).toLocaleString(locale);
    const formattedValue = value.toLocaleString(locale);

    return `${date}: ${formattedValue}${unit ? ` ${unit}` : ''}`;
}

function BuildingStatisticsTab() {
    const { locale, t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const muensterSystems = useMemo(getMuensterSystems, []);
    const [customer, setCustomer] = useState<BuildingStatisticsCustomer>(
        HAS_NEUTRAUBLING_SENSOR_DATA ? 'neutraubling' : 'muenster',
    );
    const [selectedSystem, setSelectedSystem] = useState(
        HAS_NEUTRAUBLING_SENSOR_DATA ? 'GN1' : (muensterSystems[0] ?? ''),
    );
    const [statistics, setStatistics] = useState<BuildingStatisticsSummary | null>(null);
    const [isRequesting, setIsRequesting] = useState(false);
    const [modalState, setModalState] = useState({ isOpen: false, message: '', title: '' });
    const systems = customer === 'neutraubling' ? ['GN1'] : muensterSystems;

    function handleCustomerChange(nextCustomer: BuildingStatisticsCustomer) {
        setCustomer(nextCustomer);
        setSelectedSystem(nextCustomer === 'neutraubling' ? 'GN1' : (muensterSystems[0] ?? ''));
        setStatistics(null);
    }

    function showModal(message: string) {
        setModalState({ isOpen: true, message, title: t('Building statistics') });
    }

    async function handleRequest() {
        if (!accessToken || isSessionExpired || !selectedSystem) {
            return;
        }

        setIsRequesting(true);

        try {
            const requestedSystem = customer === 'neutraubling' ? 'GN.Building' : selectedSystem;
            const { data } = await eeCheckApi.requestBuildingStatistics(accessToken, requestedSystem);

            if (!data.success || data.error || !data.data) {
                throw new Error(data.message ?? t('Building statistics could not be loaded.'));
            }

            setStatistics(summarizeBuildingStatistics(data.data));
            showModal(t('Building statistics were loaded.'));
        } catch (error) {
            if (isExpiredSessionError(error)) {
                onSessionExpired();
                return;
            }

            setStatistics(null);
            const message = error instanceof EeCheckApiError || error instanceof Error
                ? error.message
                : t('Unknown error.');

            showModal(t('Building statistics request failed: {message}', { message }));
        } finally {
            setIsRequesting(false);
        }
    }

    return (
        <div className="flex w-full flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Building statistics')}</h2>
                <p>{t('Load summarized energy statistics for every available supply type of a system.')}</p>
            </section>

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <div className="grid w-full grid-cols-1 gap-y-4 text-left md:grid-cols-2 md:divide-x md:divide-base-content">
                    <div className="flex min-h-12 items-center gap-3 md:pr-5">
                        <label className="text-sm font-semibold" htmlFor="building-statistics-customer">{t('Customer')}</label>
                        <select id="building-statistics-customer" className="select select-bordered w-auto" value={customer} onChange={(event) => handleCustomerChange(event.target.value as BuildingStatisticsCustomer)} disabled={isRequesting}>
                            {HAS_NEUTRAUBLING_SENSOR_DATA ? <option value="neutraubling">{t('Neutraubling')}</option> : null}
                            <option value="muenster">{t('Münster')}</option>
                        </select>
                    </div>
                    <div className="flex min-h-12 items-center gap-3 md:pl-5">
                        <label className="text-sm font-semibold" htmlFor="building-statistics-system">{t('System')}</label>
                        <select id="building-statistics-system" className="select select-bordered w-auto max-w-full" value={selectedSystem} onChange={(event) => setSelectedSystem(event.target.value)} disabled={isRequesting}>
                            {systems.map((system) => <option key={system} value={system}>{system}</option>)}
                        </select>
                    </div>
                </div>
            </section>

            <div>
                <button type="button" className="btn btn-primary gap-2" onClick={() => void handleRequest()} disabled={!accessToken || isSessionExpired || isRequesting || !selectedSystem}>
                    <BarChart3 className="h-4 w-4" aria-hidden="true" />
                    {isRequesting ? t('Requesting building statistics...') : t('Request building statistics')}
                </button>
            </div>

            {statistics ? SUPPLY_TYPES.map(({ accent, icon: Icon, key, label }) => {
                const supply = statistics[key];

                return (
                    <section key={key} className="dashboard-panel flex flex-col gap-4 p-5">
                        <div className="flex w-full items-start gap-3 text-left">
                            <Icon
                                className={`system-dashboard__section-icon system-dashboard__section-icon--${accent}`}
                                aria-hidden="true"
                            />
                            <div className="min-w-0">
                                <h3>{t(label)}</h3>
                                <p className="mt-1 break-all font-mono text-xs text-base-content/65">
                                    {key}: {supply?.urn ?? t('Not available')}
                                </p>
                            </div>
                        </div>
                        {supply ? (
                            <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-4">
                                {SERIES_TYPES.map((seriesKey) => {
                                    const series = supply.series[seriesKey];

                                    return (
                                        <article key={seriesKey} className="min-w-0 rounded-box border border-base-content/15 bg-base-200/60 p-4 text-left">
                                            <h4 className="font-mono text-sm">{seriesKey}</h4>
                                            <dl className="mt-3 grid gap-2 text-sm">
                                                <div><dt className="font-semibold">{t('Unit')}</dt><dd>{series.unit || t('Not available')}</dd></div>
                                                <div><dt className="font-semibold">{t('First value')}</dt><dd>{formatSeriesPoint(series.firstTimestamp, series.firstValue, series.unit, locale, t('Not available'))}</dd></div>
                                                <div><dt className="font-semibold">{t('Last value')}</dt><dd>{formatSeriesPoint(series.lastTimestamp, series.lastValue, series.unit, locale, t('Not available'))}</dd></div>
                                                <div><dt className="font-semibold">{t('Number of timestamps / values')}</dt><dd>{series.timestampCount} / {series.valueCount}</dd></div>
                                            </dl>
                                        </article>
                                    );
                                })}
                            </div>
                        ) : <p className="text-center">{t('No data is available for this supply type.')}</p>}
                    </section>
                );
            }) : <section className="dashboard-panel p-5 text-base-content/70">{t('No building statistics loaded yet.')}</section>}

            <StatusModal isOpen={modalState.isOpen} message={modalState.message} onClose={() => setModalState((current) => ({ ...current, isOpen: false }))} title={modalState.title} />
        </div>
    );
}

export default BuildingStatisticsTab;
